/**
 * ブランド詳細の「平時は文字・鉛筆で編集」の欄 (利用者指示 2026-10-30: ページでは入力欄を既定で出さない)。
 *
 * - 平時は値 (空なら emptyText) を文字で出し、鉛筆はホバー / フォーカス時だけ出す (タッチ端末は常に薄く出す)。文字を押しても編集に入る
 * - 編集中は入力欄 + ✓ / ✕ (ブランド基本情報の表と同じ見た目)。Enter (複数行は Ctrl/⌘+Enter) で決定・Esc で取り消し
 * - onCommit が false を返したら (形が正しくない等) 編集を続ける
 */
import { useState } from 'react';
import { Check, X } from 'lucide-react';
import { isComposingEnter } from '../../features/videoEditor/components/imeKey';
import '../../styles/inline-edit.css';

interface Props {
  value: string;
  /** 決定した値 (前後の空白は除く)。false を返すと編集を続ける */
  onCommit: (value: string) => boolean | void;
  /** 入力欄の読み上げ名。鉛筆は「{ariaLabel}を編集」、✓ は「{ariaLabel}を決定」 */
  ariaLabel: string;
  placeholder?: string;
  /** 値が空のときに出す文字 */
  emptyText?: string;
  multiline?: boolean;
  maxLength?: number;
  disabled?: boolean;
  /** 外側 (表示・編集とも) に足すクラス (幅の指定など) */
  className?: string;
  /** 文字の見た目 (等幅など) */
  textClassName?: string;
  /** 最初から編集中で出す (「追加」から開いたとき) */
  defaultEditing?: boolean;
  /** 取り消したとき (「追加」から開いた欄を閉じる等) */
  onCancel?: () => void;
}

export function InlineEditText({
  value, onCommit, ariaLabel, placeholder, emptyText = '未設定', multiline, maxLength, disabled, className = '', textClassName = '',
  defaultEditing = false, onCancel,
}: Props) {
  const [editing, setEditing] = useState(defaultEditing);
  const [draft, setDraft] = useState(value);

  const start = () => {
    if (disabled) return;
    setDraft(value);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    onCancel?.();
  };
  const decide = () => {
    if (onCommit(draft.trim()) === false) return;
    setEditing(false);
  };

  if (editing) {
    const common = {
      className: multiline ? 'form-textarea' : 'form-input',
      value: draft,
      placeholder,
      maxLength,
      'aria-label': ariaLabel,
      autoFocus: true,
      onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
        // IME の変換確定の Enter では決定しない (Safari は keyCode 229 で判定)
        if (isComposingEnter(e)) return;
        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) { e.preventDefault(); decide(); }
      },
    };
    return (
      <div className={`inline-edit-container inline-edit-text ${className}`}>
        {multiline
          ? <textarea {...common} rows={4} onChange={(e) => setDraft(e.target.value)} />
          : <input {...common} onChange={(e) => setDraft(e.target.value)} />}
        <button type="button" onClick={decide} className="inline-save-button" title="決定" aria-label={`${ariaLabel}を決定`}>
          <Check size={18} className="inline-action-icon" />
        </button>
        <button type="button" onClick={cancel} className="inline-cancel-button" title="キャンセル" aria-label="キャンセル">
          <X size={18} className="inline-action-icon" />
        </button>
      </div>
    );
  }

  return (
    <div className={`inline-display inline-edit-hoverable inline-edit-text ${className}`}>
      <span
        className={`inline-edit-text-value${value ? '' : ' is-empty'}${multiline ? ' is-multiline' : ''} ${textClassName}`}
        title={value || undefined}
        onClick={start}
      >
        {value || emptyText}
      </span>
      {!disabled && (
        <button type="button" onClick={start} className="inline-edit-button" title="編集" aria-label={`${ariaLabel}を編集`}>
          <img src="/pencil_1.svg" alt="" className="inline-edit-icon" />
        </button>
      )}
    </div>
  );
}
